import React, { useEffect, useState } from 'react';
import { LoaderCircle, MessageSquare } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';

const emptyForm = {
  title: ''
};

export const EditChatModal = ({ open, chat, onClose, onSave }) => {
  const { t } = useI18n();
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [titleError, setTitleError] = useState('');

  useEffect(() => {
    if (!open) {
      setSaving(false);
      setTitleError('');
      return;
    }

    if (chat) {
      setForm({
        title: chat.title || ''
      });
    } else {
      setForm(emptyForm);
    }
  }, [chat, open]);

  const handleClose = () => {
    if (saving) {
      return;
    }

    onClose();
  };

  const submitHandler = async (event) => {
    event.preventDefault();

    const trimmedTitle = form.title.trim();

    if (!trimmedTitle) {
      setTitleError(t('chatTitleRequired'));
      return;
    }

    if (saving) {
      return;
    }

    setTitleError('');
    setSaving(true);

    try {
      await onSave({
        title: trimmedTitle,
        context: chat.context || ''
      });
    } finally {
      setSaving(false);
    }
  };

  if (!chat) {
    return null;
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeDisabled={saving}
      title={t('editChat')}
      panelClassName="max-w-lg rounded-[1.75rem] p-5 md:p-6"
      contentClassName="mb-5"
      titleClassName="text-lg"
    >
      <form className="space-y-5" onSubmit={submitHandler}>
        <div className="flex items-start gap-3 rounded-2xl border border-border/80 bg-white/50 p-4 dark:bg-slate-950/20">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-border bg-accent-soft text-accent">
            <MessageSquare size={18} />
          </span>
          <p className="text-sm leading-6 text-muted">{t('editChatSubtitle')}</p>
        </div>

        <Input
          label={t('taskTitleLabel')}
          value={form.title}
          disabled={saving}
          required
          maxLength={120}
          placeholder={t('chatTitlePlaceholder')}
          onChange={(event) => {
            setForm({ ...form, title: event.target.value });
            if (titleError) {
              setTitleError('');
            }
          }}
        />
        {titleError ? (
          <p className="-mt-2 text-sm text-rose-600 dark:text-rose-300" role="alert">
            {titleError}
          </p>
        ) : null}

        <div className="flex flex-col-reverse gap-3 border-t border-border/70 pt-4 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" onClick={handleClose} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button type="submit" disabled={saving || !form.title.trim()}>
            {saving ? (
              <>
                <LoaderCircle size={16} className="animate-spin" />
                {t('updatingChat')}
              </>
            ) : (
              t('save')
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
